import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTokenClaims } from './useTokenClaims'
import { getWorkerPermissions } from '../services/permissionsService'
import { RouteDataItem } from '../types/Core/Routes'
import { routeData } from '../components/navigation/RouteData'
import { useAuthContext } from './AuthContext'
import { JobRole } from '../types/Employee/JobRole'
import { Craft } from '../types/Project/Craft'
type PermissionContextType = {
	refreshPermissions: () => Promise<void>
	getProjectRoutes: (projectRoles: JobRole[], projectCrafts: Craft[]) => RouteDataItem[]
	getPermissionBasedRoutes: (roleAllowedRoutes: RouteDataItem[], allProjectRoutes: RouteDataItem[]) => RouteDataItem[]
	hasPermission: (permissionId: number) => boolean
	hasAnyPermissions: (permissionIds: number[]) => boolean
	/** False until the initial permission fetch finishes (or is skipped when not authenticated). */
	permissionsReady: boolean
}

const PermissionContext = createContext<PermissionContextType | null>(null)

const usePermissionContext = (): PermissionContextType => {
	const permissionContextState = useContext(PermissionContext)
	if (permissionContextState === null) {
		throw new Error(
			'PermissionContext not found. Try wrapping a parent component with <PermissionProvider>.',
		)
	}
	return permissionContextState
}

type PermissionProviderProps = {
	children?: React.ReactNode
}

const PermissionProvider = ({ children }: PermissionProviderProps) => {
	const [permissions, setPermissions] = useState<number[]>([])
	const [permissionsReady, setPermissionsReady] = useState(false)
	const tokenClaims = useTokenClaims()
	const { hasWpaToken } = useAuthContext()

	const fetchPermissions = async () => {
		if (!tokenClaims.id || !hasWpaToken()) {
			setPermissions([])
			setPermissionsReady(false)
			return
		}

		setPermissionsReady(false)
		try {
			const workerPermissions = await getWorkerPermissions(tokenClaims.id)
			setPermissions(workerPermissions || [])
		} catch (error) {
			console.error('Failed to fetch permissions:', error)
			setPermissions([])
		} finally {
			setPermissionsReady(true)
		}
	}

	const getProjectRoutes = (projectRoles: JobRole[], projectCrafts: Craft[]) => {
		let routes =
			projectRoles && projectRoles.some((r) => r.title === 'QualityControl')
				? routeData
				: routeData.filter(
					(route) => route.title !== 'Inspection Backlog' && route.title !== 'Inspections',
				)

		routes =
			projectRoles && projectRoles.some((r) => r.title === 'LineTech')
				? routes
				: routes.filter((route) => route.title !== 'Surveys')

		routes =
			projectRoles && projectRoles.some((r) => r.title === 'MaterialManager')
				? routes
				: routes.filter((route) => route.title !== 'Material Preparation')
		routes =
			projectCrafts && projectCrafts.some((c) => c.name === 'Scaffold')
				? routes
				: routes.filter((route) => route.title !== 'Scaffold Orders')
		return routes
	}

	const getPermissionBasedRoutes = (
		roleAllowedRoutes: RouteDataItem[],
		allProjectRoutes: RouteDataItem[],
	): RouteDataItem[] => {
		const isSameRouteEntry = (a: RouteDataItem, b: RouteDataItem) =>
			a.path === b.path &&
			a.roles.length === b.roles.length &&
			a.roles.every((role) => b.roles.includes(role))

		const excludedRoutes = allProjectRoutes.filter(
			(route) => !roleAllowedRoutes.some((r) => isSameRouteEntry(r, route)),
		)
		const permissionRoutes = excludedRoutes.filter((route) =>
			permissions.some((p) => route.requiredPermissions?.includes(p)),
		)
		return permissionRoutes
	}

	useEffect(() => {
		fetchPermissions()
	}, [tokenClaims.id])

	const refreshPermissions = async () => {
		await fetchPermissions()
	}

	const hasPermission = (permissionId: number) => {
		return permissions.includes(permissionId)
	}

	const hasAnyPermissions = (permissionIds: number[]) => {
		return permissions.some((p) => permissionIds.includes(p))
	}

	return (
		<PermissionContext.Provider
			value={{
				refreshPermissions,
				getPermissionBasedRoutes,
				getProjectRoutes,
				hasPermission,
				hasAnyPermissions,
				permissionsReady,
			}}
		>
			{children}
		</PermissionContext.Provider>
	)
}

export { PermissionProvider, usePermissionContext }
